import { GridColumn as Column } from '@progress/kendo-react-grid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  DeleteButton,
  SecondaryButton
} from 'src/components/shared/Button/Button';
import { BooleanCell } from 'src/components/shared/DataGrid/BooleanCell';
import { BooleanFilterCell } from 'src/components/shared/DataGrid/BooleanFilterCell';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { GridCountCell } from 'src/components/shared/DataGrid/GridCountCell';
import {
  HeaderSection,
  HeaderSectionLeft,
  HeaderSectionRight
} from 'src/components/shared/HeaderSection/HeaderSection';
import { Modal } from 'src/components/shared/Modal/Modal';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { Header1 } from 'src/components/shared/Typography/Header1';
import { useIsFormDirty } from 'src/hooks/useIsFormDirty';
import { useReduxDispatch } from 'src/store';
import {
  PackagingDetailDto,
  PackagingDto
} from 'src/store/packagings/packaging';
import { mapPackagingDetailToEdit } from 'src/store/packagings/packaging.map';
import {
  createPackaging,
  deletePackaging,
  editPackaging,
  fetchPackagings
} from 'src/store/packagings/packagings.actions';
import { PackagingApi } from 'src/store/packagings/packagings.api';
import { selectPackagings } from 'src/store/packagings/packagings.selectors';
import { isLoadingPackagings } from 'src/store/requests/requests.selectors';
import { PackagingForm } from './PackagingForm';

export const PackagingsPage = () => {
  const [currentPackaging, setCurrentPackaging] = useState<PackagingDto | null>(
    null
  );
  const [packaging, setPackaging] =
    useState<Nullable<PackagingDetailDto>>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [isFormDirty, callbackRef] = useIsFormDirty();

  const { t } = useTranslation();
  const dispatch = useReduxDispatch();

  const packagings = useSelector(selectPackagings);
  const loadingPackagings = useSelector(isLoadingPackagings);

  useEffect(() => {
    dispatch(fetchPackagings());
  }, [dispatch]);

  useEffect(() => {
    if (currentPackaging) {
      setShowDeleteModal(true);
    }
  }, [currentPackaging]);

  const onCancel = () => {
    setCurrentPackaging(null);
    setPackaging(null);
    setShowDeleteModal(false);
    setShowEditModal(false);
  };

  const onCreate = () => {
    setShowEditModal(true);
  };

  const onEdit = async (packaging: PackagingDto) => {
    const response = await PackagingApi.getById(packaging.id);
    if (response.ok) {
      setPackaging(response.data);
      setShowEditModal(true);
    }
  };

  const onDelete = (packaging: PackagingDto) => {
    setCurrentPackaging(packaging);
  };

  const onConfirmDelete = () => {
    if (currentPackaging) {
      dispatch(deletePackaging(currentPackaging.id, currentPackaging));
      setCurrentPackaging(null);
      setShowDeleteModal(false);
    }
  };

  const onSubmit = async (packaging: PackagingDetailDto) => {
    const dto = mapPackagingDetailToEdit(packaging);
    if (dto.id) {
      await dispatch(editPackaging(dto.id, dto));
      onCancel();
    } else {
      await dispatch(createPackaging(dto));
      onCancel();
    }
  };

  return (
    <>
      <HeaderSection className="mb-32">
        <HeaderSectionLeft>
          <Header1>{t('packaging.plural')}</Header1>
          <BodyType>{`${t('crudtext')} ${t('packaging.plural')}`}</BodyType>
        </HeaderSectionLeft>
        <HeaderSectionRight>
          <SecondaryButton onClick={onCreate} icon="la-plus">
            {`${t('create.verb')} ${t('packaging.singular')}`}
          </SecondaryButton>
        </HeaderSectionRight>
      </HeaderSection>

      <DataGrid
        items={packagings}
        loading={loadingPackagings}
        onRowClick={({ dataItem }) => onEdit(dataItem)}
        initialSort={[{ field: 'no', dir: 'asc' }]}
      >
        <Column
          field="no"
          width={200}
          title={t('props.number')}
          footerCell={() => (
            <GridCountCell
              count={packagings?.length}
              label={t('packaging.plural')}
            />
          )}
        />
        <Column field="name" title={t('props.name')} />
        <Column field="description" title={t('props.description')} />
        <Column field="weight" title={t('props.weight')} />
        <Column
          width="160px"
          field="active"
          title={t('props.active')}
          cell={props => <BooleanCell {...props} />}
          filterCell={props => <BooleanFilterCell {...props} />}
        />
        <Column
          width="100px"
          filterable={false}
          cell={props => (
            <td>
              <DeleteButton onClick={() => onDelete(props.dataItem)} />
            </td>
          )}
        />
      </DataGrid>

      <Modal
        header={`${packaging ? t('edit') : t('create.verb')} ${t(
          'packaging.singular'
        )}`}
        confirmText={t('save.verb')}
        onClose={() => onCancel()}
        open={showEditModal}
        allowCommonExitStrategies={!isFormDirty}
      >
        <PackagingForm
          packaging={packaging}
          onCancel={onCancel}
          onSubmit={onSubmit}
          ref={callbackRef}
        />
      </Modal>

      <Modal
        width={500}
        header={`${t('delete.verb')} ${t('packaging.singular')}`}
        confirmText={t('delete.verb')}
        onClose={() => onCancel()}
        onConfirm={() => onConfirmDelete()}
        open={showDeleteModal}
        showActions
      >
        <BodyType>
          {t('packaging.delete')} <q>{currentPackaging?.name}</q>?
        </BodyType>
      </Modal>
    </>
  );
};
